import { Link } from 'react-router-dom'

import type { PageAdItem, PageAdLayout, PageAdPage } from '@/api/page-ads'
import { usePageAds } from '@/features/page-ads/hooks/usePageAds'
import { resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'
import { cn } from '@/shared/lib/utils'

const LAYOUT_ORDER: PageAdLayout[] = ['fullscreen', 'row_1', 'row_2', 'row_4']

/** 广告位独立网格（row_4 = 一行 4 张，不与目录列表列数绑定） */
const GRID_CLASS: Record<PageAdLayout, string> = {
  fullscreen: 'grid-cols-1',
  row_1: 'grid-cols-1',
  row_2: 'grid-cols-1 sm:grid-cols-2',
  row_4: 'grid-cols-2 lg:grid-cols-4',
}

const ROW_GAP = 'gap-2 lg:gap-2.5'

function sortGroups(groups: { layout: PageAdLayout; items: PageAdItem[] }[]) {
  const rank = new Map(LAYOUT_ORDER.map((layout, index) => [layout, index]))
  return [...groups].sort(
    (a, b) => (rank.get(a.layout) ?? 99) - (rank.get(b.layout) ?? 99),
  )
}

function AdImage({ item }: { item: PageAdItem }) {
  const src = resolveMediaDisplayUrl(item.image_url)
  if (!src) return null

  const img = (
    <img
      src={src}
      alt={item.title || ''}
      className="block h-auto w-full max-w-full select-none"
      loading="lazy"
      draggable={false}
    />
  )

  if (item.link_url) {
    if (item.link_url.startsWith('/')) {
      return (
        <Link to={item.link_url} className="block min-w-0">
          {img}
        </Link>
      )
    }
    return (
      <a
        href={item.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block min-w-0"
      >
        {img}
      </a>
    )
  }

  return <div className="min-w-0">{img}</div>
}

function AdTile({ item }: { item: PageAdItem }) {
  return (
    <figure className="min-w-0">
      <AdImage item={item} />
    </figure>
  )
}

/** 全屏广告：贴边全宽，无内边距/边框 */
function FullBleedWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2">
      {children}
    </div>
  )
}

export function PageAdsBlock({
  groups,
  variant = 'inline',
}: {
  groups: { layout: PageAdLayout; items: PageAdItem[] }[]
  variant?: 'fullscreen' | 'inline'
}) {
  const sorted = sortGroups(groups.filter((g) => g.items.length > 0))
  if (sorted.length === 0) return null

  const body = (
    <div className={cn('w-full', variant === 'fullscreen' ? 'space-y-0' : 'space-y-2')}>
      {sorted.map((group) => (
        <div
          key={group.layout}
          className={cn('grid w-full', ROW_GAP, GRID_CLASS[group.layout])}
        >
          {group.items.map((item) => (
            <AdTile key={item.id} item={item} />
          ))}
        </div>
      ))}
    </div>
  )

  if (variant === 'fullscreen') {
    return <FullBleedWrap>{body}</FullBleedWrap>
  }

  return <div className="mb-4 w-full">{body}</div>
}

export function PageAdsSection({ page, className }: { page: PageAdPage; className?: string }) {
  const { groups, loading } = usePageAds(page)
  if (loading) return null
  return <PageAdsFromGroups groups={groups} className={className} />
}

export function PageAdsFromGroups({
  groups,
  className,
}: {
  groups: { layout: PageAdLayout; items: PageAdItem[] }[]
  className?: string
}) {
  const fullscreen = groups.filter((g) => g.layout === 'fullscreen' && g.items.length > 0)
  const inline = groups.filter((g) => g.layout !== 'fullscreen' && g.items.length > 0)
  if (fullscreen.length === 0 && inline.length === 0) return null

  return (
    <div className={cn('w-full', className)}>
      {fullscreen.length > 0 ? (
        <PageAdsBlock groups={fullscreen} variant="fullscreen" />
      ) : null}
      {inline.length > 0 ? <PageAdsBlock groups={inline} variant="inline" /> : null}
    </div>
  )
}
